"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import { userFacingErrorMessage } from "@/utils/extract-rtk-error";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import {
  useConciliateDivergencesMutation,
  useGetDivergenceQuery,
  useUpdateDivergenceBatchMutation
} from "@/services/divergence";
import { useGetOpenConciliationsForStoreQuery } from "@/services/store-conciliation";
import { transformObjectToArrayByKey } from "@/utils/formData";
import { numberToCurrency } from "@/utils/money";
import { LoadingDots } from "components/loadingDots";
import { Button } from "components/ui/button";
import { Card } from "components/ui/card";
import { toast } from "components/ui/use-toast";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import type {
  IDivergence,
  IDivergenceAccountEntriesFormData,
} from "src/@types/divergence";
import type { Paginated } from "src/@types/paginated";
import { MESSAGES } from "../../utils/messages";
import { useCurrentConciliation } from "../components/divergence-list/ducks/filter/hooks/statusConciliation";
import { useColumns } from "./columns";
import { GroupedTable } from "./grouped-table";
import MultiDateFilter from "./multi-date-filter";
import { conciliationSchema } from "./schema";

interface AccountingEntriesProps {
  store_code: string;
  conciliation_date: string;
  onStateChange?: (hasChanges: boolean) => void;
}

export default function AccoutingEntries({ store_code, conciliation_date, onStateChange }: AccountingEntriesProps) {
  const [conciliatedDivergence, { isLoading: IsConciliationLoading }] =
    useConciliateDivergencesMutation();

  const [updateDivergenceBatch, { isLoading: isUpdateLoading }] =
    useUpdateDivergenceBatchMutation();

  const { id: conciliation_id, isClosed } = useCurrentConciliation();

  const methods = useForm();
  const {
    handleSubmit: handleFormSubmit,
    formState: { errors },
    watch,
  } = methods;

  const [formErrors] = useState<
    { [field: string]: string } | undefined
  >();

  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [appliedDates, setAppliedDates] = useState<Date[]>([]);
  const [conciliationError, setConciliationError] = useState<string>("");

  const columns = useColumns(
    methods,
    formErrors,
    showCheckboxes,
    selectedItems,
    (newSelectedItems) => {
      setSelectedItems(newSelectedItems);
      setConciliationError("");
    }
  );

  const { handleModal } = useDialogModal();

  const {
    data: response,
    isLoading,
    isFetching,
  } = useGetDivergenceQuery({
    search: "",
    conciliation_store_id: conciliation_id as string,
    store_code: store_code || "",
    dates: appliedDates.map(date => format(date, "yyyy-MM-dd")),
    meta: {
      page: 1,
    },
  });

  const { data: openedConciliations } = useGetOpenConciliationsForStoreQuery({
    storeCode: Number(store_code),
    conciliationDate: conciliation_date
  });

  const allowedDates: Date[] = Array.isArray(openedConciliations)
    ? openedConciliations
      .map((d: string) => {
        const [day, month, year] = d.split("/");
        return new Date(Number(year), Number(month) - 1, Number(day));
      })
      .filter((d) => !isNaN(d.getTime()))
    : [];

  const { data } = response ?? ({} as Paginated<IDivergence>);

  const handleSave = async (data: IDivergenceAccountEntriesFormData) => {
    const divergenceArray = transformObjectToArrayByKey(data, "divergence");

    const formattedDivergenceArray = divergenceArray.map((d: any) => {
      const divergenceId = d.value.id;

      const justificationFieldName = `divergence.justification_${divergenceId}`;
      const justificationId = methods.watch(justificationFieldName);

      return {
        id: divergenceId,
        version: d.value.version,
        account_notes: d.value.observation ?? "",
        justification_id: justificationId ? Number(justificationId) : undefined
      };
    }).filter((d => d.id === undefined || d.id === null ? false : true));

    if (formattedDivergenceArray.length === 0 || isUpdateLoading) {
      toast(MESSAGES.INFOS_FILLED.ERROR);
      return;
    }

    try {
      await updateDivergenceBatch({
        conciliation_id: Number(conciliation_id),
        divergences: formattedDivergenceArray
      }).unwrap();
      setSelectedItems(new Set());
      onStateChange?.(true);
      toast(MESSAGES.UPDATE.SUCCESS);
    } catch (error) {
      toast({
        ...MESSAGES.UPDATE.ERROR,
        description: userFacingErrorMessage(
          error,
          MESSAGES.UPDATE.ERROR.description
        ),
      });
    }
  };

  useEffect(() => {
    if (selectedItems.size > 0 && data) {
      const selectedDivergences = data.filter((item: IDivergence) =>
        selectedItems.has(item.id.toString())
      );

      const justificationFields = selectedDivergences.map(item =>
        `divergence.justification_${item.id}`
      );

      const subscription = watch((_, { name }) => {
        if (name && justificationFields.includes(name)) {

          const amounts = selectedDivergences.map(item => Number(item.amount) || 0);
          const hasPositive = amounts.some(amount => amount > 0);
          const hasNegative = amounts.some(amount => amount < 0);

          if ((hasPositive && !hasNegative) || (hasNegative && !hasPositive)) {
            setConciliationError("Para conciliar, selecione valores de sinais opostos (positivos e negativos que se compensem).");
            return;
          }

          const divergencesData = selectedDivergences.map(item => ({
            id: Number(item.id),
            justification_id: methods.getValues(`divergence.justification_${item.id}`)
              ? Number(methods.getValues(`divergence.justification_${item.id}`))
              : undefined,
            operator: item.operator,
            amount: Number(item.amount)
          }));

          const result = conciliationSchema.safeParse({ divergences: divergencesData });

          if (result.success) {
            setConciliationError("");
            document.querySelectorAll('[data-needs-justification]').forEach(el => {
              el.classList.remove('needs-justification');
            });
          } else {
            setConciliationError(result.error.errors[0].message);
          }
        }
      });

      return () => subscription.unsubscribe();
    }
  }, [selectedItems, data, watch, methods]);

  const handleConciliateDivergence = async () => {
    const selectedDivergences = data
      ?.filter((item: IDivergence) => selectedItems.has(item.id.toString())) || [];

    if (selectedDivergences.length === 0) {
      setConciliationError("Selecione pelo menos um item para conciliar.");
      return;
    }

    const amounts = selectedDivergences.map(item => Number(item.amount) || 0);
    const hasPositive = amounts.some(amount => amount > 0);
    const hasNegative = amounts.some(amount => amount < 0);

    if ((hasPositive && !hasNegative) || (hasNegative && !hasPositive)) {
      setConciliationError("Para conciliar, selecione valores de sinais opostos (positivos e negativos que se compensem).");
      return;
    }

    const divergencesData = selectedDivergences.map(item => ({
      id: Number(item.id),
      justification_id: methods.getValues(`divergence.justification_${item.id}`)
        ? Number(methods.getValues(`divergence.justification_${item.id}`))
        : undefined,
      operator: item.operator,
      amount: Number(item.amount)
    }));

    const result = conciliationSchema.safeParse({ divergences: divergencesData });

    if (!result.success) {
      setConciliationError(result.error.errors[0].message);

      const itemsNeedingJustification = selectedDivergences
        .filter(item => !methods.getValues(`divergence.justification_${item.id}`))
        .map(item => item.id.toString());

      document.querySelectorAll('[data-needs-justification]').forEach(el => {
        el.classList.remove('needs-justification');
      });

      itemsNeedingJustification.forEach(id => {
        document.querySelectorAll(`[data-needs-justification="${id}"]`).forEach(el => {
          el.classList.add('needs-justification');
        });
      });

      return;
    }

    setConciliationError("");

    try {
      await conciliatedDivergence({
        divergences: selectedDivergences.map(item => ({
          id: Number(item.id),
          justification_id: Number(methods.getValues(`divergence.justification_${item.id}`)) || undefined
        }))
      }).unwrap();
      setShowCheckboxes(false);
      setSelectedItems(new Set());
      setConciliationError("");
      onStateChange?.(true);
      toast(MESSAGES.UPDATE.SUCCESS);
    } catch (error) {
      toast({
        ...MESSAGES.UPDATE.ERROR,
        description: userFacingErrorMessage(
          error,
          MESSAGES.UPDATE.ERROR.description
        ),
      });
    }
  };

  const totalSelectedAmount =
    data
      ?.filter((item: IDivergence) => selectedItems.has(item.id.toString()))
      .reduce(
        (total: number, item: IDivergence) => total + Number(item.amount),
        0
      ) || 0;

  return (
    <>
      <div className="flex flex-col w-full overflow-hidden">
        <div className="flex items-center gap-2 py-2 bg-white">
          <MultiDateFilter
            onSelect={undefined}
            onApply={(dates) => setAppliedDates(dates)}
            appliedDates={appliedDates}
            allowedDates={allowedDates}
            defaultDate={conciliation_date}
          />
          {appliedDates.length > 0 && (
            <Button
              variant="outline"
              onClick={() => {
                setAppliedDates([]);
              }}
              className="h-9"
            >
              Limpar Filtros
            </Button>
          )}
        </div>
        <div className="flex-1 min-h-0">
          <div className="border relative rounded-md">
            <div className="max-h-[500px] overflow-x-auto relative">
              <GroupedTable
                data={data}
                columns={columns}
              />
            </div>
          </div>
        </div>
        <div className="flex-col py-2 bg-white border-t flex justify-end gap-2">
          {conciliationError && (
            <Card className="w-full rounded-lg text-sm p-3 bg-red-50 border-red-200">
              <div className="flex items-center gap-2 text-red-700">
                <span className="font-medium">⚠️ Erro de Validação:</span>
                <span>{conciliationError}</span>
              </div>
            </Card>
          )}
          {showCheckboxes && (
            <Card className="w-full rounded-lg text-sm p-2">
              <div className="flex items-center justify-between">
                <div className="flex-1 flex items-center gap-2 overflow-x-auto">
                  {selectedItems.size > 0 ? (
                    data
                      ?.filter((item: IDivergence) =>
                        selectedItems.has(item.id.toString())
                      )
                      .map((item: IDivergence) => (
                        <div
                          key={item.id}
                          className="flex-shrink-0 bg-gray-100 rounded-md px-2 py-1 border border-gray-200"
                          title={`ID: ${item.id}`}
                        >
                          {numberToCurrency(item.amount)}
                        </div>
                      ))
                  ) : (
                    <span className="text-gray-400">Nenhum item selecionado</span>
                  )}
                </div>
                <div className="flex items-center gap-4 ml-4">
                  <div className="font-medium text-sm">
                    Total:{" "}
                    <span className="">
                      {numberToCurrency(totalSelectedAmount)}
                    </span>
                  </div>
                  <Button
                    className="save rounded-md disabled:bg-[#F6D040]/40 text-[#4B303C]"
                    disabled={
                      selectedItems.size === 0 ||
                      IsConciliationLoading ||
                      isClosed === true || Boolean(conciliationError)
                    }
                    onClick={handleConciliateDivergence}
                  >
                    Conciliar Itens
                  </Button>
                </div>
              </div>
            </Card>
          )}
          <div className="flex items-center justify-between gap-3 mt-4 p-3 bg-[#F9FBFC] rounded-lg border">
            <Button
              variant="outline"
              // type="button"
              onClick={() => {
                handleModal({
                  isOpen: false,
                });
              }}
            >
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCheckboxes(!showCheckboxes);
                  setConciliationError("");
                }}
                disabled={isClosed === true || isFetching || isLoading}
              >
                {showCheckboxes ? "Ocultar Conciliação" : "Conciliar Divergências"}
              </Button>
              <Button
                className="rounded-md"
                disabled={
                  JSON.stringify(errors ?? {}) !== "{}" ||
                  isLoading ||
                  isFetching ||
                  isClosed === true
                }
                onClick={() => handleFormSubmit(handleSave)()}
              >
                <LoadingDots
                  isLoading={isUpdateLoading || isFetching}
                  text="Salvar"
                />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
